import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resetConsent } from "@/lib/consent";
import { SITE } from "@/lib/site";

const LAST_UPDATED = "8 de outubro de 2026";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-titillium font-bold text-xl lg:text-2xl text-[#1A2B56] mb-4">
      {title}
    </h2>
    <div className="space-y-4 text-[15px] leading-relaxed text-slate-700">
      {children}
    </div>
  </section>
);

const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-2">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const EmailLink = () => (
  <a
    href={`mailto:${SITE.email}`}
    data-testid="privacy-email-link"
    className="font-semibold text-[#1A2B56] underline underline-offset-4 hover:text-[#e63946] transition-colors"
  >
    {SITE.email}
  </a>
);

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = "Política de Privacidade | Buzas";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <main
      data-testid="privacy-policy-page"
      className="bg-white text-[#1A2B56] min-h-screen flex flex-col"
    >
      <header className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="privacy-logo-link"
            aria-label="Buzas — Soluções Industriais"
            className="flex items-center"
          >
            <img
              src={SITE.logos.header}
              alt="Buzas — Soluções Industriais"
              className="h-12 lg:h-14 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A2B56] hover:text-[#e63946] transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={2} />
            Voltar ao site
          </Link>
        </div>
      </header>

      <article className="flex-1 max-w-3xl w-full mx-auto px-4 md:px-8 py-12 lg:py-16">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#e63946] font-bold mb-3">
          LGPD
        </div>
        <h1 className="font-titillium font-bold text-3xl lg:text-4xl text-[#1A2B56]">
          Política de Privacidade
        </h1>
        <p className="mt-3 text-sm text-slate-500">
          Última atualização: {LAST_UPDATED}
        </p>

        <div className="mt-8 space-y-4 text-[15px] leading-relaxed text-slate-700">
          <p>
            A {SITE.legalName} (“Buzas”) respeita a sua privacidade. Esta
            Política explica quais dados pessoais coletamos neste site, para
            que os usamos, com quem os compartilhamos e quais são os seus
            direitos, de acordo com a Lei Geral de Proteção de Dados (Lei nº
            13.709/2018 — LGPD).
          </p>
        </div>

        <Section title="1. Quem é o controlador dos dados">
          <p>
            O controlador dos dados pessoais tratados neste site é a{" "}
            {SITE.legalName}, com sede em {SITE.location}. Para qualquer
            assunto relacionado a privacidade e proteção de dados, fale com a
            gente pelo e-mail <EmailLink />.
          </p>
        </Section>

        <Section title="2. Quais dados coletamos">
          <p>
            <strong>Dados que você nos envia.</strong> Ao preencher o
            formulário de orçamento, coletamos:
          </p>
          <List
            items={[
              "Nome",
              "Empresa (opcional)",
              "E-mail",
              "Telefone / WhatsApp",
              "Mensagem com informações sobre o seu projeto (opcional)",
            ]}
          />
          <p>
            Se você preferir falar conosco pelo WhatsApp, a conversa acontece
            na plataforma do WhatsApp, que tem os seus próprios termos e
            política de privacidade.
          </p>
          <p>
            <strong>Dados de navegação.</strong> Somente se você aceitar os
            cookies, coletamos informações sobre o uso do site, como páginas
            visitadas, cliques, rolagem, tipo de dispositivo e navegador,
            endereço IP e localização aproximada. Essa coleta pode incluir a
            gravação da sessão de navegação (reprodução das suas interações
            com as páginas).
          </p>
        </Section>

        <Section title="3. Para que usamos os dados">
          <List
            items={[
              "Responder ao seu pedido de orçamento e dar continuidade ao atendimento comercial e técnico.",
              "Entrar em contato por e-mail, telefone ou WhatsApp sobre a sua solicitação.",
              "Entender como o site é usado e melhorar o seu conteúdo e funcionamento.",
              "Cumprir obrigações legais e regulatórias e exercer direitos em processos.",
            ]}
          />
        </Section>

        <Section title="4. Bases legais">
          <p>Tratamos os seus dados com base nas seguintes hipóteses da LGPD:</p>
          <List
            items={[
              "Procedimentos preliminares a um contrato, a seu pedido (art. 7º, V) — para responder ao pedido de orçamento.",
              "Consentimento (art. 7º, I) — para os cookies e ferramentas de análise de navegação.",
              "Legítimo interesse (art. 7º, IX) — para manter o relacionamento comercial e a segurança do site.",
              "Cumprimento de obrigação legal ou regulatória (art. 7º, II).",
            ]}
          />
        </Section>

        <Section title="5. Cookies e tecnologias semelhantes">
          <p>
            Usamos armazenamento local do navegador para lembrar a sua escolha
            sobre cookies. Isso é necessário para o funcionamento do aviso e
            não depende de consentimento.
          </p>
          <p>
            Os cookies de análise de navegação (ferramenta PostHog) só são
            ativados depois que você clica em “Aceitar” no aviso de cookies.
            Se você recusar, o site funciona normalmente e nenhuma informação
            de navegação é enviada a essa ferramenta.
          </p>
          <p>Você pode mudar de ideia a qualquer momento:</p>
          <Button
            type="button"
            variant="outline"
            onClick={resetConsent}
            data-testid="privacy-review-cookies"
            className="rounded-sm h-11 px-5 font-semibold text-sm border-[#1A2B56] text-[#1A2B56] hover:bg-[#1A2B56] hover:text-white"
          >
            Rever preferências de cookies
          </Button>
        </Section>

        <Section title="6. Com quem compartilhamos">
          <p>
            Não vendemos os seus dados pessoais. Eles podem ser compartilhados
            apenas com:
          </p>
          <List
            items={[
              "Fornecedores que operam o site em nosso nome, como serviços de hospedagem, banco de dados e análise de navegação.",
              "Autoridades públicas, quando houver obrigação legal ou ordem judicial.",
            ]}
          />
          <p>
            Alguns desses fornecedores ficam fora do Brasil, inclusive nos
            Estados Unidos. Nesses casos, a transferência internacional é
            feita conforme as regras da LGPD.
          </p>
        </Section>

        <Section title="7. Por quanto tempo guardamos">
          <p>
            Guardamos os dados enviados pelo formulário pelo tempo necessário
            para atender à sua solicitação e manter o relacionamento
            comercial, e depois disso pelo prazo exigido para cumprir
            obrigações legais ou resguardar direitos. Quando deixam de ser
            necessários, os dados são eliminados ou anonimizados.
          </p>
        </Section>

        <Section title="8. Seus direitos">
          <p>Como titular dos dados, a LGPD garante a você o direito de:</p>
          <List
            items={[
              "Confirmar se tratamos os seus dados e acessá-los.",
              "Corrigir dados incompletos, inexatos ou desatualizados.",
              "Pedir a anonimização, o bloqueio ou a eliminação de dados desnecessários ou excessivos.",
              "Pedir a portabilidade dos dados.",
              "Pedir a eliminação dos dados tratados com base no seu consentimento.",
              "Saber com quem compartilhamos os seus dados.",
              "Revogar o consentimento a qualquer momento.",
              "Opor-se a um tratamento que considere irregular.",
            ]}
          />
          <p>
            Para exercer qualquer um desses direitos, escreva para{" "}
            <EmailLink />. Você também pode apresentar reclamação à Autoridade
            Nacional de Proteção de Dados (ANPD).
          </p>
        </Section>

        <Section title="9. Segurança">
          <p>
            Adotamos medidas técnicas e administrativas para proteger os dados
            pessoais contra acessos não autorizados, perda, alteração ou
            divulgação indevida. Nenhum sistema é totalmente imune a riscos;
            se ocorrer um incidente que possa causar dano relevante, vamos
            comunicar os titulares afetados e a ANPD, como determina a lei.
          </p>
        </Section>

        <Section title="10. Alterações nesta Política">
          <p>
            Podemos atualizar esta Política para refletir mudanças no site ou
            na legislação. A data da última atualização fica sempre indicada
            no topo desta página.
          </p>
        </Section>

        <Section title="11. Contato">
          <p>
            Dúvidas sobre esta Política ou sobre o tratamento dos seus dados:{" "}
            <EmailLink />.
          </p>
        </Section>
      </article>

      <footer className="bg-[#1A2B56] text-slate-400">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs">
            © {new Date().getFullYear()} {SITE.legalName}.
          </p>
          <Link
            to="/"
            className="text-xs hover:text-white transition-colors"
          >
            Voltar ao site
          </Link>
        </div>
      </footer>
    </main>
  );
}
